import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, AlertTriangle, CheckSquare, Clock, Hourglass, ListChecks } from "lucide-react";
import { getStaffDashboard } from "@/lib/dashboardApi";

type SalesBoardItem = {
  proposal_id: string;
  case_number: string;
  title?: string | null;
  client_name?: string | null;
  status: string;
  priority?: string | null;
  updated_at?: string | null;
  expiry_date?: string | null;
  status_age_business_days?: number | null;
  stale_follow_up?: boolean | null;
  days_since_last_follow_up?: number | null;
};

type SalesBoardColumn = {
  status: string;
  label: string;
  items: SalesBoardItem[];
};

const SalesDashboard = () => {
  const { userRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeCount, setActiveCount] = useState(0);
  const [dueTodayCount, setDueTodayCount] = useState(0);
  const [overdueCount, setOverdueCount] = useState(0);
  const [staleCount, setStaleCount] = useState(0);
  const [needsAttentionCount, setNeedsAttentionCount] = useState(0);
  const [columns, setColumns] = useState<SalesBoardColumn[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await getStaffDashboard();
        setActiveCount(response.active_cases_count ?? 0);
        setDueTodayCount(response.due_today_count ?? 0);
        setOverdueCount(response.overdue_count ?? 0);
        setStaleCount(response.stale_count ?? 0);
        setNeedsAttentionCount(response.needs_attention_count ?? 0);
        setColumns((response.board_columns ?? []) as SalesBoardColumn[]);
      } catch (e) {
        console.error("Error loading sales dashboard:", e);
        setColumns([]);
      } finally {
        setLoading(false);
      }
    };

    if (userRole === "sales_staff") {
      load();
    }
  }, [userRole]);

  const priorityBadge = (priority?: string | null) => {
    const key = (priority || "medium").toLowerCase();
    const variant =
      key === "high" ? "destructive" : key === "low" ? "outline" : "secondary";
    const label = key.toUpperCase();
    return <Badge variant={variant}>{label}</Badge>;
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatBusinessDays = (value?: number | null) => {
    if (value === null || value === undefined) return "—";
    if (value <= 0) return "Today";
    return `${value} bd`;
  };

  const boardEmpty = useMemo(
    () => columns.every((col) => (col.items ?? []).length === 0),
    [columns]
  );

  if (userRole !== "sales_staff") {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-muted-foreground">Sales dashboard is only available for Sales Staff.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Sales Dashboard</h2>
        <p className="text-muted-foreground">Operational view of your proposals and priorities.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">My Active Proposals</CardTitle>
            <CheckSquare className="h-4 w-4 text-sales" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? "—" : activeCount}</div>
            <p className="text-xs text-muted-foreground">Assigned to you</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Due Today</CardTitle>
            <Clock className="h-4 w-4 text-finance" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? "—" : dueTodayCount}</div>
            <p className="text-xs text-muted-foreground">Needs action today</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
            <AlertCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? "—" : overdueCount}</div>
            <p className="text-xs text-muted-foreground">Immediate attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Stale</CardTitle>
            <Hourglass className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? "—" : staleCount}</div>
            <p className="text-xs text-muted-foreground">No updates in 7+ days</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Needs Attention</CardTitle>
            <ListChecks className="h-4 w-4 text-operations" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? "—" : needsAttentionCount}</div>
            <p className="text-xs text-muted-foreground">Sent or needs fix</p>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">My Proposal Board</h3>
          <span />
        </div>

        <div className="rounded-xl border border-border bg-card/30 p-4">
          <div className="flex gap-4 overflow-x-auto pb-2">
            {columns.map((column, idx) => (
              <div
                key={column.status}
                className="min-w-[260px] max-w-[260px] relative"
              >
                {idx !== 0 && (
                  <div className="absolute -left-2 top-0 h-full w-px bg-border/60" />
                )}
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-sm font-semibold">{column.label}</div>
                  <Badge variant="outline">{column.items.length}</Badge>
                </div>
                <div className="space-y-2 border border-dashed border-border/70 rounded-lg p-2 bg-background/40">
                {loading && (
                  <div className="text-xs text-muted-foreground">Loading...</div>
                )}
                {!loading && column.items.length === 0 && (
                  <div className="text-xs text-muted-foreground">No items</div>
                )}
                {!loading &&
                  column.items.map((item) => (
                    <Link key={item.proposal_id} to={`/proposals/${item.proposal_id}`}>
                      <div
                        className={`rounded-md border bg-card p-3 shadow-sm transition-colors ${
                          item.stale_follow_up
                            ? "border-red-500 ring-2 ring-red-500/60 bg-red-50/60 hover:bg-red-50"
                            : "border-border hover:bg-muted/50"
                        }`}
                      >
                        {item.stale_follow_up && (
                          <div className="mb-2 flex items-center gap-1 rounded-md bg-red-600 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm animate-pulse">
                            <AlertTriangle className="h-3 w-3" />
                            No follow-up {item.days_since_last_follow_up ? `${item.days_since_last_follow_up}d` : "5d+"}
                          </div>
                        )}
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-sm font-medium">{item.case_number}</div>
                          {priorityBadge(item.priority)}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {item.title || item.client_name || "Untitled"}
                        </div>
                        <div className="mt-2 text-[11px] text-muted-foreground flex justify-between">
                          <span>Updated: {formatDate(item.updated_at)}</span>
                          <span>Due: {formatDate(item.expiry_date)}</span>
                        </div>
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          In this status for {formatBusinessDays(item.status_age_business_days)}
                        </div>
                      </div>
                    </Link>
                  ))}
              </div>
              </div>
            ))}
          </div>
        </div>

        {!loading && boardEmpty && (
          <div className="text-sm text-muted-foreground mt-4">
            No proposals assigned to you yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesDashboard;
